import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';

import { Button } from 'components/shared/Button';
import { Typography } from 'components/shared/Typography';

import { Tooltip } from 'components/Tooltip';

import {
  formatDuration,
  formatRelativeDayTitle,
  formatSessionTime,
  getScreenLabel,
  type ExitCause,
  type JourneySummaryStats,
} from '../../TimelineSection.helpers';

import styles from './JourneySummary.module.scss';

const EMPTY_VALUE = '—';

const SummaryStat = ({
  className,
  label,
  value,
  valueClassName,
  children,
}: {
  className?: string;
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
  children?: React.ReactNode;
}) => (
  <div className={cn(styles.stat, className)}>
    <Typography
      className={styles.label}
      variant="body3"
      weight="medium"
      mode="compact"
    >
      {label}
    </Typography>
    <div className={cn(styles.value, valueClassName)}>{value}</div>
    <div className={styles.text}>{children}</div>
  </div>
);

const JourneySummary = ({
  summary,
  story = [],
  onCauseSessionShow,
}: {
  summary: JourneySummaryStats;
  /** «За 90 дней — 12 сессий, чаще в плагине. 3 раза ушёл из‑за ошибки — …» */
  story?: string[];
  onCauseSessionShow: (cause: ExitCause) => void;
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const { lastSession, topExitScreen } = summary;
  const topExitLabel = topExitScreen
    ? getScreenLabel(topExitScreen.eventName, topExitScreen.properties)
    : null;

  const renderNumber = (value: React.ReactNode) => (
    <Typography variant="subtitle" weight="semiBold">
      {value}
    </Typography>
  );

  const renderSecondaryText = (text: string) => (
    <Typography className={styles.secondaryText} variant="body3" mode="compact">
      {text}
    </Typography>
  );

  const renderCauseLink = (cause: ExitCause, count: number) =>
    count > 0 ? (
      <Button
        className={styles.showButton}
        variant="link"
        size="sm"
        onClick={() => onCauseSessionShow(cause)}
      >
        {getTranslation('student_page.journey.summary.show')}
      </Button>
    ) : (
      renderSecondaryText(getTranslation('student_page.journey.summary.none'))
    );

  return (
    <div className={styles.summary}>
      <SummaryStat
        label={getTranslation('student_page.journey.summary.last_visit')}
        value={renderNumber(
          lastSession
            ? formatSessionTime(lastSession.startedAt, locale)
            : EMPTY_VALUE,
        )}
      >
        {lastSession
          ? renderSecondaryText(
              getTranslation('student_page.journey.summary.last_visit_text', {
                date: formatRelativeDayTitle(lastSession.startedAt, locale),
                platform: getTranslation(
                  `common.text.${lastSession.platform.toLowerCase()}`,
                ),
              }),
            )
          : null}
      </SummaryStat>

      <SummaryStat
        label={getTranslation('student_page.journey.summary.sessions')}
        value={renderNumber(summary.totalSessions)}
      >
        {renderSecondaryText(
          getTranslation('student_page.journey.summary.median', {
            duration: formatDuration(summary.medianDurationMs),
          }),
        )}
      </SummaryStat>

      <SummaryStat
        label={getTranslation('student_page.journey.summary.with_result')}
        value={renderNumber(`${summary.goalPercent}%`)}
      >
        {renderSecondaryText(
          getTranslation('student_page.journey.summary.with_result_text', {
            count: summary.goalCount,
            total: summary.totalSessions,
          }),
        )}
      </SummaryStat>

      <SummaryStat
        label={getTranslation('student_page.journey.summary.left_on_error')}
        value={renderNumber(summary.errorCount)}
        valueClassName={cn({ [styles.valueError]: summary.errorCount > 0 })}
      >
        {renderCauseLink('error', summary.errorCount)}
      </SummaryStat>

      <SummaryStat
        label={getTranslation('student_page.journey.summary.left_stuck')}
        value={renderNumber(summary.stuckCount)}
        valueClassName={cn({ [styles.valueWarning]: summary.stuckCount > 0 })}
      >
        {renderCauseLink('stuck', summary.stuckCount)}
      </SummaryStat>

      <SummaryStat
        label={getTranslation('student_page.journey.summary.top_exit')}
        value={
          topExitLabel ? (
            <Tooltip
              text={topExitLabel}
              triggerClassName={styles.screenTrigger}
              triggerElement={
                <Typography
                  className={styles.screenValue}
                  variant="body1"
                  weight="semiBold"
                  mode="compact"
                >
                  {topExitLabel}
                </Typography>
              }
            />
          ) : (
            renderNumber(EMPTY_VALUE)
          )
        }
      >
        {topExitScreen
          ? renderSecondaryText(
              /* Plural by the total: «в 1 из 1 сессии», «в 2 из 5 сессий». */
              getTranslation('student_page.journey.summary.top_exit_text', {
                ended: topExitScreen.total,
                count: summary.totalSessions,
              }),
            )
          : null}
      </SummaryStat>

      {story.length > 0 ? (
        <Typography className={styles.story} variant="body3">
          {story.join(' ')}
        </Typography>
      ) : null}
    </div>
  );
};

export default React.memo(JourneySummary) as any;
